<template>
  <div class="flex flex-col">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-semibold">Campañas de Email</h2>

      <router-link
        v-if="hasPermission('manage_emails')"
        :to="{ name: 'massEmailsForm' }"
        class="primary-button"
      >
        Nueva Campaña
      </router-link>
    </div>

    <!-- Filter tabs -->
    <div class="flex space-x-2 mt-6">
      <button
        v-for="status in statuses"
        :key="status.value"
        @click="filterByStatus(status.value)"
        :class="[
          'px-4 py-2 rounded-full text-sm font-medium smooth',
          selectedStatus === status.value
            ? 'bg-primary-500 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-100'
        ]"
      >
        {{ status.label }}
      </button>
    </div>

    <!-- Campaigns table -->
    <div v-if="!loading" class="card mt-6">
      <div v-if="campaigns.length > 0" class="overflow-x-auto">
        <table class="w-full table-auto">
          <thead>
            <tr class="bg-primary-100 text-left">
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">ID</th>
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Asunto</th>
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Audiencia</th>
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Destinatarios</th>
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado</th>
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Programado</th>
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Enviado</th>
              <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="campaign in campaigns"
              :key="campaign.id"
              class="table-row"
            >
              <td class="table-cell text-sm">#{{ campaign.id }}</td>
              <td class="table-cell">
                <router-link
                  :to="{ name: 'massEmailsDetail', params: { id: campaign.id } }"
                  class="text-primary-500 hover:text-primary-600 font-medium"
                >
                  {{ campaign.subject }}
                </router-link>
              </td>
              <td class="table-cell text-sm">
                <span class="px-2 py-1 rounded-full bg-gray-100 text-xs">
                  {{ formatAudienceType(campaign.audience_type) }}
                </span>
              </td>
              <td class="table-cell text-sm">
                <div class="flex flex-col text-xs">
                  <span class="font-medium">{{ campaign.total_recipients }}</span>
                  <span v-if="campaign.status === 'sent'" class="text-gray-500">
                    {{ campaign.sent_count }} enviados
                  </span>
                </div>
              </td>
              <td class="table-cell">
                <status-badge :status="campaign.status" :label="formatStatus(campaign.status)" />
              </td>
              <td class="table-cell text-sm text-gray-600">
                {{ campaign.scheduled_at ? formatDate(campaign.scheduled_at) : '-' }}
              </td>
              <td class="table-cell text-sm text-gray-600">
                {{ campaign.sent_at ? formatDate(campaign.sent_at) : '-' }}
              </td>
              <td class="table-cell">
                <div class="flex space-x-2">
                  <router-link
                    :to="{ name: 'massEmailsDetail', params: { id: campaign.id } }"
                    class="text-primary-500 hover:text-primary-600 text-sm"
                  >
                    Ver
                  </router-link>

                  <router-link
                    v-if="campaign.status === 'draft'"
                    :to="{ name: 'massEmailsForm', params: { id: campaign.id } }"
                    class="text-primary-500 hover:text-primary-600 text-sm"
                  >
                    Editar
                  </router-link>

                  <button
                    v-if="campaign.status === 'draft' && hasPermission('manage_emails')"
                    @click="deleteCampaign(campaign)"
                    class="text-error-500 hover:text-error-600 text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-12 text-gray-500">
        <p class="text-lg">No hay campañas {{ selectedStatus ? 'con este estado' : '' }}</p>
        <router-link
          v-if="hasPermission('manage_emails')"
          :to="{ name: 'massEmailsForm' }"
          class="primary-button mt-4"
        >
          Crear Primera Campaña
        </router-link>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else class="card mt-6 py-12 text-center">
      <div class="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
      <p class="text-gray-500 mt-4">Cargando campañas...</p>
    </div>

    <!-- Delete confirmation modal -->
    <delete-modal
      v-if="showDeleteModal"
      :loading="loadingDelete"
      @close="closeDeleteModal()"
      @confirmDelete="confirmDelete"
    />
  </div>
</template>

<script>
import api from '@/api';
import permissionsMixin from '@/mixins/permissionsMixin';
import deleteMixin from '@/mixins/deleteMixin';
import DeleteModal from '@/components/ui/DeleteModal.vue';

export default {
  name: 'MassEmailsIndex',

  components: {
    DeleteModal,
  },

  mixins: [permissionsMixin, deleteMixin],

  data() {
    return {
      campaigns: [],
      loading: false,
      loadingDelete: false,
      selectedStatus: null,
      campaignToDelete: null,
      statuses: [
        { value: null, label: 'Todas' },
        { value: 'draft', label: 'Borrador' },
        { value: 'scheduled', label: 'Programadas' },
        { value: 'sending', label: 'Enviando' },
        { value: 'sent', label: 'Enviadas' },
        { value: 'failed', label: 'Fallidas' },
      ],
    };
  },

  mounted() {
    this.loadCampaigns();
  },

  methods: {
    async loadCampaigns() {
      this.loading = true;
      try {
        const params = [];
        if (this.selectedStatus) {
          params.push({ name: 'status', value: this.selectedStatus });
        }

        const response = await api.massEmails.getAllWithQueryParams(params);
        this.campaigns = response.data;
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al cargar campañas');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    filterByStatus(status) {
      this.selectedStatus = status;
      this.loadCampaigns();
    },

    formatAudienceType(type) {
      const types = {
        cats: 'Solo Gatos',
        dogs: 'Solo Perros',
        both: 'Gatos y Perros',
        pending_quotes: 'Cotizaciones Pendientes',
        cancelled_quotes: 'Cotizaciones Canceladas',
        active_quotes: 'Cotizaciones Activas',
        everyone: 'Todos',
      };
      return types[type] || type;
    },

    formatStatus(status) {
      const statuses = {
        draft: 'Borrador',
        scheduled: 'Programada',
        sending: 'Enviando',
        sent: 'Enviada',
        failed: 'Fallida',
      };
      return statuses[status] || status;
    },

    getStatusClass(status) {
      const classes = {
        draft: 'px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium',
        scheduled: 'px-2 py-1 rounded-full bg-tertiary-100 text-tertiary-700 text-xs font-medium',
        sending: 'px-2 py-1 rounded-full bg-quaternary-100 text-quaternary-700 text-xs font-medium',
        sent: 'px-2 py-1 rounded-full bg-success-100 text-success-700 text-xs font-medium',
        failed: 'px-2 py-1 rounded-full bg-error-100 text-error-700 text-xs font-medium',
      };
      return classes[status] || classes.draft;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    deleteCampaign(campaign) {
      this.campaignToDelete = campaign;
      this.openDeleteModal();
    },

    async confirmDelete() {
      this.loadingDelete = true;
      try {
        await api.massEmails.delete(this.campaignToDelete.id);
        this.$store.dispatch('successSnack', 'Campaña eliminada exitosamente');
        await this.loadCampaigns();
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al eliminar campaña');
        console.error(error);
      } finally {
        this.loadingDelete = false;
        this.closeDeleteModal();
        this.campaignToDelete = null;
      }
    },
  },
};
</script>
